/**
 * The debug history, grouped the way an investigation reads it.
 *
 * A warm transfer is two records and a four-party conference is five, but each
 * is *one thing that happened*. Listing them flat asks the reader to rebuild
 * that relationship from call ids; grouping states it.
 */

import { SCENARIO_LABELS } from 'Modules/softphone/components/SoftphoneDevTools/constants';
import type { DevToolsBadgeVariant } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsStatusBadge/index.types';
import { getInteractionId } from 'Modules/softphone/components/SoftphoneDevTools/utils/countInteractionLegs';
import { getLegHealth } from 'Modules/softphone/components/SoftphoneDevTools/utils/getLegHealth';
import type { CallDebugInfo } from 'Modules/softphone/services/DebugService';

export interface DebugInteraction {
  id: string;
  /** Group heading: `warm transfer`, `conference`, `direct call`. */
  title: string;
  /** `referred`, `merged`, `clean` — the worst verdict among the legs. */
  outcome: string;
  outcomeVariant: DevToolsBadgeVariant;
  /** When the interaction began, taken from its earliest leg. */
  startedAt: string | null;
  legs: CallDebugInfo[];
}

/**
 * Worst-first, so a group is labelled by its problem rather than by whichever
 * leg happened to be captured first.
 */
const VARIANT_SEVERITY: Record<DevToolsBadgeVariant, number> = {
  error: 4,
  warning: 3,
  info: 2,
  neutral: 1,
  success: 0,
  primary: 0,
};

/** The group heading. A plain call has no scenario, and says so in words. */
const getInteractionTitle = (legs: CallDebugInfo[]): string => {
  const scenario = legs.find(
    (leg) => leg.scenario && leg.scenario !== 'direct'
  )?.scenario;

  if (!scenario) {
    return 'direct call';
  }

  // A conference is one interaction even though its legs carry two different
  // scenario keys ('conference' per participant, 'conference-room' for the room).
  if (scenario === 'conference' || scenario === 'conference-room') {
    return 'conference';
  }

  return SCENARIO_LABELS[scenario] ?? scenario;
};

const getEarliestStart = (legs: CallDebugInfo[]): string | null =>
  legs.reduce<string | null>((earliest, leg) => {
    if (!leg.startTime) {
      return earliest;
    }

    if (!earliest) {
      return leg.startTime;
    }

    return new Date(leg.startTime) < new Date(earliest)
      ? leg.startTime
      : earliest;
  }, null);

export const groupDebugInteractions = (
  history: CallDebugInfo[]
): DebugInteraction[] => {
  // A Map keeps first-appearance order, so the grouped list stays in the same
  // order as the flat history it replaced.
  const groups = new Map<string, CallDebugInfo[]>();

  history.forEach((info) => {
    const id = getInteractionId(info);
    const legs = groups.get(id);

    if (legs) {
      legs.push(info);
      return;
    }

    groups.set(id, [info]);
  });

  return Array.from(groups.entries()).map(([id, legs]) => {
    const worst = legs
      .map(getLegHealth)
      .reduce((a, b) =>
        VARIANT_SEVERITY[b.variant] > VARIANT_SEVERITY[a.variant] ? b : a
      );

    return {
      id,
      title: getInteractionTitle(legs),
      outcome: worst.label,
      outcomeVariant: worst.variant,
      startedAt: getEarliestStart(legs),
      legs,
    };
  });
};
